import { useCallback } from 'react'
import { RoomTypes } from '@weblite/ts-action/room'
import { FormatListBulletedIcon } from '@weblite/icons'
import { styled } from '@mui/material/styles'
import { SendField } from '$comp/_app/components/chat/components/footer/components/send-field'
import { ActionButton } from '$comp/_app/components/chat/components/footer/components/action-button'
import { showLeaveHallModal } from '$comp/modals/confirmation-dialog'
import { useOpenedRoomPermissions } from '$logic/opened-room'
import { joinHall, setSubscriptionMute } from '$logic/subscription'
import { useHall, useRoomHasCourseVitrine } from '$logic/hall/react-hook'
import { isClientsRoom } from '$logic/room'
import { t } from '$i18n'
import { ArrowIOSIcon } from '$common/icons'
import { useSubscription } from '$logic/subscription/react-hook'
import { switchToGeneralIssue } from '$logic/router'
import { CourseVitrineWappPanel } from '$logic/wapp-panels'

export function HallFooter({
  roomId,
  roomType,
}: {
  roomId: string
  roomType: RoomTypes
}) {
  const isGroup = roomType === 'GROUP'

  const { entity: hall, loading: hallLoading } = useHall(roomId)

  const { loading: subscriptionLoading, entity: subscription } =
    useSubscription(roomId)

  const { openedRoomPermissions } = useOpenedRoomPermissions()
  const { sendMessage, sendMedia, sendWapp } = openedRoomPermissions || {}
  const hasPermissionToSendMessage = !!(sendMessage || sendMedia || sendWapp)

  const courseVitrineEnabled = useRoomHasCourseVitrine(roomId)
  const channelCourseButtonIsVisible =
    courseVitrineEnabled && !hasPermissionToSendMessage

  const subscribed = isClientsRoom(roomId)

  const onNotificationClick = useCallback(async () => {
    await setSubscriptionMute(roomId, !subscription?.muted)
  }, [roomId, subscription?.muted])

  const onJoinClick = useCallback(async () => {
    analite('CHAT_JOIN', { source: 'HALL_FOOTER', name: hall?.identifier })
    await joinHall(roomId)
  }, [roomId, hall?.identifier])

  const onLeave = useCallback(async () => {
    await showLeaveHallModal(roomId)
  }, [roomId])

  const onGeneralIssueButtonClick = () => {
    analite('CHAT_ISSUE_CLICK', { source: 'HALL_FOOTER' })
    switchToGeneralIssue()
  }

  if (hallLoading || (subscribed && subscriptionLoading)) return null

  return hall?.deletedAt ? (
    <ActionButton
      text={isGroup ? t('menu.hall.leaveGroup') : t('menu.hall.leaveChannel')}
      onClick={onLeave}
    />
  ) : !subscribed ? (
    <ActionButton
      text={
        isGroup
          ? t('profile.hall.actions.joinGroup')
          : t('profile.hall.actions.joinChannel')
      }
      onClick={onJoinClick}
    />
  ) : !sendMessage ? (
    channelCourseButtonIsVisible ? (
      <ChannelVitrineActionButton
        startIcon={<FormatListBulletedIcon />}
        onClick={() => CourseVitrineWappPanel.open({ roomId, isAdmin: false })}
        endIcon={
          <ArrowIOSIcon
            sx={{ color: 'neutral900', width: '20px', height: '20px' }}
          />
        }
        text={t('chat.courseVitrine')}
      />
    ) : (
      <ActionButton
        text={subscription?.muted ? t('chat.unMute') : t('chat.mute')}
        onClick={onNotificationClick}
      />
    )
  ) : (
    <SendField roomId={roomId} />
  )
}

// <ActionButton // NOTE: while !sendMessage will be this not null
//   startIcon={<VitrineIcon sx={{ color: 'neutral900' }} />}
//   endIcon={<ArrowIOSIcon sx={{ color: 'neutral900' }} />}
//   text={t('chat.generalIssue')}
//   onClick={onGeneralIssueButtonClick}
//   backgroundColor="yellow500"
//   color="neutral900"
// />

const ChannelVitrineActionButton = styled(ActionButton)`
  background-color: ${({ theme }) => theme.palette.yellow500};
  &:hover {
    background-color: ${({ theme }) => theme.palette.yellow500};
  }
  border-top: unset;
  color: ${({ theme }) => theme.palette.neutral900};
  .MuiButton-endIcon {
    margin-right: auto;
  }
  .MuiButton-startIcon {
    margin: 0 0 0 8px;
  }
`
